import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    formatDeadlineHeaderLabel,
    formatDeadlineLabel,
    getDeadlineOptions,
} from "../closeTemplateTasksDrawer.constants";
import {
    CloseTemplateTaskCallbacks,
    CloseTemplateTaskRow,
} from "../closeTemplateTasksViewConfig";

type Meta = ViewTableMeta<CloseTemplateTaskCallbacks, never>;

export const CloseTemplateTaskDeadlineCell = memo(
    function CloseTemplateTaskDeadlineCell({
        row,
        table,
    }: CellContext<ViewTableFeatures, CloseTemplateTaskRow, number | null>) {
        const meta = (table.options.meta ?? {}) as Meta;
        const item = row.original;

        if (item.isGroupRow) {
            return null;
        }

        const options = getDeadlineOptions(item.externalDeadline);
        const deadlineLabel =
            item.externalDeadline != null
                ? formatDeadlineLabel(item.externalDeadline)
                : undefined;

        return (
            <ViewTableCell>
                <Picker.Select<number>
                    value={item.externalDeadline}
                    onValueChange={(v) =>
                        meta.callbacks?.onUpdateDeadline(item.id, v)
                    }
                >
                    <Picker.CellContent
                        value={deadlineLabel}
                        addText="Set deadline"
                        hideAddIcon
                    />
                    <Picker.Popup>
                        <Picker.List>
                            <Picker.Section>
                                <Picker.SectionTitle>
                                    {formatDeadlineHeaderLabel(
                                        item.deadlineAnchor,
                                    )}
                                </Picker.SectionTitle>
                                <Picker.Item
                                    onSelect={() => {
                                        if (item.externalDeadline !== null) {
                                            meta.callbacks?.onUpdateDeadline(
                                                item.id,
                                                null,
                                            );
                                        }
                                    }}
                                >
                                    None
                                </Picker.Item>
                                {options.map((days) => (
                                    <Picker.Item key={days} value={days}>
                                        {formatDeadlineLabel(days)}
                                    </Picker.Item>
                                ))}
                            </Picker.Section>
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);
